const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/eCheckinItems.ts';
import { KisApiAction, ReducerState } from '@cistec/common_web/defs/actionTypes';
import { KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import {
    actionLoadECheckinItems,
    CREATE_ECHECKIN,
    FINALIZE_ECHECKIN,
    LOAD_ECHECKIN_ITEMS,
    UPDATE_ECHECKIN,
} from 'App/actions/eCheckinItems';
import {
    defaultKisApiActionSuccess,
    getData,
    getRequestStatus,
    makeReducer2,
    removeStructure,
    RequestStatus,
    useRemoteData2,
} from '@cistec/common_web/utils/redux4Requests';
import { ECheckinWorkItem } from '@cistec/common_web/defs/kisim';
import { PatAppStoreState } from './setup/rootReducer';
import { createSelector } from 'reselect';
import moment from 'moment';
import Imm from 'immutable';

const KEY_ITEM_LIST = 'list';
const KEY_ITEM_BY_FALL_AND_TKEY = 'mapByFallNrAndTKey';
export const DEFAULT_ECHECKIN_WORKITEM_TKEY = 'PATNR';
export const KEY_NO_FALL_NUMBER = 'NO_FALL_NUMBER';

const getFinalizeRequestStatusKey = (fallNr?: string, tKey?: string) => [
    KEY_ITEM_BY_FALL_AND_TKEY,
    fallNr ?? KEY_NO_FALL_NUMBER,
    tKey ?? DEFAULT_ECHECKIN_WORKITEM_TKEY,
    'finalize',
];

function getDataAsArray<T>(state: ReducerState, keys: string[]): T[] {
    return state.getIn([...keys, 'data'])?.toArray() || [];
}

const getSortedECheckinWorkItemsFromState = (
    state: ReducerState,
    fallNr: string,
    tKey: string = DEFAULT_ECHECKIN_WORKITEM_TKEY
): ECheckinWorkItem[] => {
    const workItems = getDataAsArray<ECheckinWorkItem>(state, [KEY_ITEM_LIST]);

    return workItems
        .filter(workItem => workItem.FALLNR === fallNr && workItem.TKEY === tKey)
        .sort((a, b) => moment(b.DAT, KISIM_TIME_FORMAT).diff(moment(a.DAT, KISIM_TIME_FORMAT)));
};

const makeEcheckinListUniqueByFallNrAndTKey = (items: ECheckinWorkItem[]): ECheckinWorkItem[] => {
    const map = new Map<string, ECheckinWorkItem>();

    items.forEach(item => {
        const key = `${item.FALLNR}::${item.TKEY}`;
        const existing = map.get(key);

        if (!existing || moment(item.DAT, KISIM_TIME_FORMAT).isAfter(moment(existing.DAT, KISIM_TIME_FORMAT))) {
            map.set(key, item);
        }
    });

    return [...map.values()];
};

function handleLoadECheckinItemsSuccess(state: ReducerState, action: KisApiAction) {
    state = removeStructure(state, [KEY_ITEM_LIST]);

    state = defaultKisApiActionSuccess(state, Imm.List(makeEcheckinListUniqueByFallNrAndTKey(action.payload)), [
        KEY_ITEM_LIST,
    ]);
    return state;
}

function updateECheckinList(state: ReducerState, workItem: ECheckinWorkItem): ReducerState {
    const workItemsList = getDataAsArray<ECheckinWorkItem>(state, [KEY_ITEM_LIST])
        // remove Current workItem
        .filter(item => item.WORKID !== workItem.WORKID)
        // add new WorkItem
        .concat(workItem);

    state = state.setIn(['list', 'data'], Imm.List(workItemsList));
    state = state.setIn(
        getFinalizeRequestStatusKey(workItem.FALLNR, workItem.TKEY),
        Imm.Map({
            requestStatus: RequestStatus.SUCCESS,
        })
    );

    return state;
}

function handleUpdateECheckinItemsSuccess(state: ReducerState, action: KisApiAction) {
    const { WORKID, NewWorkItem } = action.payload;

    const newWorkItemArray = getDataAsArray<ECheckinWorkItem>(state, [KEY_ITEM_LIST])
        .filter(item => item.WORKID !== WORKID)
        .concat(NewWorkItem);

    return state.setIn(['list', 'data'], Imm.List(newWorkItemArray));
}

function handleFinalizeECheckinItemsSuccess(state: ReducerState, action: KisApiAction) {
    const workItem: ECheckinWorkItem = action.payload;

    const updated = getDataAsArray<ECheckinWorkItem>(state, [KEY_ITEM_LIST])
        .filter(item => item.WORKID !== workItem.WORKID)
        .concat(workItem);

    return state
        .setIn(['list', 'data'], Imm.List(updated))
        .setIn(
            getFinalizeRequestStatusKey(workItem.FALLNR, workItem.TKEY),
            Imm.Map({ requestStatus: RequestStatus.SUCCESS })
        );
}

function handleCreateECheckinItemsSuccess(state: ReducerState, action: KisApiAction) {
    return updateECheckinList(state, action.payload);
}

const getFinalizeEcheckinHandlerFunction =
    (requestStatus: RequestStatus) => (state: ReducerState, action: KisApiAction) =>
        state.setIn(getFinalizeRequestStatusKey(action.meta.fallNr, action.meta.tKey), Imm.Map({ requestStatus }));

export const eCheckinItemsReducer = makeReducer2([
    [LOAD_ECHECKIN_ITEMS, [KEY_ITEM_LIST], undefined, handleLoadECheckinItemsSuccess],
    [CREATE_ECHECKIN, [KEY_ITEM_LIST], undefined, handleCreateECheckinItemsSuccess],

    [UPDATE_ECHECKIN, [KEY_ITEM_LIST], undefined, handleUpdateECheckinItemsSuccess],
    [
        FINALIZE_ECHECKIN,
        [KEY_ITEM_LIST],
        undefined,
        handleFinalizeECheckinItemsSuccess,
        getFinalizeEcheckinHandlerFunction(RequestStatus.PENDING),
        getFinalizeEcheckinHandlerFunction(RequestStatus.FAILED),
    ],
]);

// SELECTORS

const selectReducerState = (state: PatAppStoreState) => state.eCheckinItems;

const selectECheckinItems = createSelector([selectReducerState], (state: ReducerState): ECheckinWorkItem[] =>
    getData(state, [KEY_ITEM_LIST])?.toArray()
);

export const makeSelectECheckinItem = (
    fallNr: string = KEY_NO_FALL_NUMBER,
    tKey: string = DEFAULT_ECHECKIN_WORKITEM_TKEY
) =>
    createSelector(
        [selectReducerState],
        (state: ReducerState) => getSortedECheckinWorkItemsFromState(state, fallNr, tKey)?.[0]
    );

export const makeSelectFinalizeECheckinRequestStatus = (
    fallNr: string = KEY_NO_FALL_NUMBER,
    tKey: string = DEFAULT_ECHECKIN_WORKITEM_TKEY
) =>
    createSelector([selectReducerState], (state: ReducerState) =>
        getRequestStatus(state, getFinalizeRequestStatusKey(fallNr, tKey))
    );

// HOOKS

export function useECheckinItem(fallNr: string = KEY_NO_FALL_NUMBER, tKey: string = DEFAULT_ECHECKIN_WORKITEM_TKEY) {
    return useRemoteData2('eCheckinItems', {
        request: dispatch => dispatch(actionLoadECheckinItems()),
        keys: [KEY_ITEM_LIST],
        select: makeSelectECheckinItem(fallNr, tKey || DEFAULT_ECHECKIN_WORKITEM_TKEY),
    });
}

export function useECheckinItems() {
    return useRemoteData2('eCheckinItems', {
        request: dispatch => dispatch(actionLoadECheckinItems()),
        keys: [KEY_ITEM_LIST],
        select: selectECheckinItems,
    });
}
